import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createBillingAccessApiService } from '../services/BillingAccessApiService';
import type {
  BillingAccount,
  BillingCreditLedgerEntry,
  BillingProduct,
  BillingProjectSummary,
  BillingSubscription,
  BillingUsageMeter,
  CreateManualBillingSubscriptionInput,
  UpdateBillingSubscriptionInput,
} from '../types';

export interface UseBillingCatalogResult {
  products: BillingProduct[];
  meters: BillingUsageMeter[];
  projects: BillingProjectSummary[];
  projectSubscriptions: BillingSubscription[];
  projectAccount: BillingAccount | null;
  projectCreditLedger: BillingCreditLedgerEntry[];
  loading: boolean;
  saving: boolean;
  projectsLoading: boolean;
  subscriptionsLoading: boolean;
  projectCreditsLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  searchProjects: (query?: string) => Promise<void>;
  createProduct: (input: Partial<BillingProduct>) => Promise<BillingProduct>;
  updateProduct: (productId: string, input: Partial<BillingProduct>) => Promise<BillingProduct>;
  archiveProduct: (productId: string) => Promise<BillingProduct>;
  createMeter: (input: Partial<BillingUsageMeter>) => Promise<BillingUsageMeter>;
  loadProjectSubscriptions: (projectId: string) => Promise<void>;
  createManualSubscription: (
    projectId: string,
    input: CreateManualBillingSubscriptionInput,
  ) => Promise<BillingSubscription>;
  updateProjectSubscription: (
    projectId: string,
    subscriptionId: string,
    input: UpdateBillingSubscriptionInput,
  ) => Promise<BillingSubscription>;
  deleteProjectSubscription: (projectId: string, subscriptionId: string) => Promise<void>;
  loadProjectCreditState: (projectId: string) => Promise<void>;
  grantProjectCredits: (
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ) => Promise<BillingCreditLedgerEntry>;
  debitProjectCredits: (
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ) => Promise<BillingCreditLedgerEntry>;
}

export function useBillingCatalog(autoLoad = true): UseBillingCatalogResult {
  const service = useMemo(() => createBillingAccessApiService(), []);
  const projectSearchRequestRef = useRef(0);
  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [meters, setMeters] = useState<BillingUsageMeter[]>([]);
  const [projects, setProjects] = useState<BillingProjectSummary[]>([]);
  const [projectSubscriptions, setProjectSubscriptions] = useState<BillingSubscription[]>([]);
  const [projectAccount, setProjectAccount] = useState<BillingAccount | null>(null);
  const [projectCreditLedger, setProjectCreditLedger] = useState<BillingCreditLedgerEntry[]>([]);
  const [loading, setLoading] = useState(autoLoad);
  const [saving, setSaving] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [projectCreditsLoading, setProjectCreditsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextProducts, nextMeters] = await Promise.all([
        service.listProducts(),
        service.listMeters(),
      ]);
      setProducts(nextProducts);
      setMeters(nextMeters);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [service]);

  const searchProjects = useCallback(async (query = '') => {
    const requestId = projectSearchRequestRef.current + 1;
    projectSearchRequestRef.current = requestId;
    setProjectsLoading(true);
    setError(null);
    try {
      const nextProjects = await service.listProjects(query);
      if (requestId === projectSearchRequestRef.current) {
        setProjects(nextProjects);
      }
    } catch (err) {
      if (requestId === projectSearchRequestRef.current) {
        setError((err as Error).message);
      }
    } finally {
      if (requestId === projectSearchRequestRef.current) {
        setProjectsLoading(false);
      }
    }
  }, [service]);

  useEffect(() => {
    if (autoLoad) {
      refresh();
      searchProjects();
    }
  }, [autoLoad, refresh, searchProjects]);

  const createProduct = useCallback(async (input: Partial<BillingProduct>) => {
    setSaving(true);
    setError(null);
    try {
      const product = await service.createProduct(input);
      await refresh();
      return product;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh, service]);

  const updateProduct = useCallback(async (productId: string, input: Partial<BillingProduct>) => {
    setSaving(true);
    setError(null);
    try {
      const product = await service.updateProduct(productId, input);
      await refresh();
      return product;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh, service]);

  const archiveProduct = useCallback(async (productId: string) => {
    setSaving(true);
    setError(null);
    try {
      const product = await service.archiveProduct(productId);
      await refresh();
      return product;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh, service]);

  const createMeter = useCallback(async (input: Partial<BillingUsageMeter>) => {
    setSaving(true);
    setError(null);
    try {
      const meter = await service.createMeter(input);
      await refresh();
      return meter;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [refresh, service]);

  const loadProjectSubscriptions = useCallback(async (projectId: string) => {
    if (!projectId) {
      setProjectSubscriptions([]);
      return;
    }
    setSubscriptionsLoading(true);
    setProjectSubscriptions([]);
    setError(null);
    try {
      setProjectSubscriptions(await service.listProjectSubscriptions(projectId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [service]);

  const loadProjectCreditState = useCallback(async (projectId: string) => {
    if (!projectId) {
      setProjectAccount(null);
      setProjectCreditLedger([]);
      return;
    }
    setProjectCreditsLoading(true);
    setError(null);
    try {
      const [account, ledger] = await Promise.all([
        service.getAccount(projectId),
        service.listCreditLedger(projectId, 25),
      ]);
      setProjectAccount(account);
      setProjectCreditLedger(ledger);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setProjectCreditsLoading(false);
    }
  }, [service]);

  const createManualSubscription = useCallback(async (
    projectId: string,
    input: CreateManualBillingSubscriptionInput,
  ) => {
    setSaving(true);
    setError(null);
    try {
      const subscription = await service.createManualSubscription(projectId, input);
      await loadProjectSubscriptions(projectId);
      return subscription;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadProjectSubscriptions, service]);

  const updateProjectSubscription = useCallback(async (
    projectId: string,
    subscriptionId: string,
    input: UpdateBillingSubscriptionInput,
  ) => {
    setSaving(true);
    setError(null);
    try {
      const subscription = await service.updateProjectSubscription(projectId, subscriptionId, input);
      await loadProjectSubscriptions(projectId);
      return subscription;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadProjectSubscriptions, service]);

  const deleteProjectSubscription = useCallback(async (projectId: string, subscriptionId: string) => {
    setSaving(true);
    setError(null);
    try {
      await service.deleteProjectSubscription(projectId, subscriptionId);
      await loadProjectSubscriptions(projectId);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadProjectSubscriptions, service]);

  const grantProjectCredits = useCallback(async (
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ) => {
    setSaving(true);
    setError(null);
    try {
      const entry = await service.grantProjectCredits(projectId, input);
      await loadProjectCreditState(projectId);
      return entry;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadProjectCreditState, service]);

  const debitProjectCredits = useCallback(async (
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ) => {
    setSaving(true);
    setError(null);
    try {
      const entry = await service.debitProjectCredits(projectId, input);
      await loadProjectCreditState(projectId);
      return entry;
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadProjectCreditState, service]);

  return {
    products,
    meters,
    projects,
    projectSubscriptions,
    projectAccount,
    projectCreditLedger,
    loading,
    saving,
    projectsLoading,
    subscriptionsLoading,
    projectCreditsLoading,
    error,
    refresh,
    searchProjects,
    createProduct,
    updateProduct,
    archiveProduct,
    createMeter,
    loadProjectSubscriptions,
    createManualSubscription,
    updateProjectSubscription,
    deleteProjectSubscription,
    loadProjectCreditState,
    grantProjectCredits,
    debitProjectCredits,
  };
}
